import { Award, BookOpen, Brain, Crown, Footprints, GraduationCap, Layers, ScrollText, Sparkles, Trophy, Zap, type LucideIcon } from 'lucide-react';
import { LEARN_PATHS, pathProgress } from './learnPaths';
import type { QuizAttempt } from '@/hooks/useQuizAttempts';

export type Badge = {
  key: string;
  title: string;
  description: string;
  icon: LucideIcon;
  /** Tailwind text color class for the icon */
  accent: string;
  /** Tailwind bg class for the icon tile */
  tile: string;
  category: 'Certificate' | 'Milestone' | 'Quiz' | 'Mastery';
  isEarned: (ctx: BadgeContext) => boolean;
  /** Optional progress (0..1) for locked badges */
  progress?: (ctx: BadgeContext) => number;
};

export type BadgeContext = {
  isComplete: (key: string) => boolean;
  attempts: QuizAttempt[];
};

function passedQuizzes(attempts: QuizAttempt[]): string[] {
  const set = new Set<string>();
  for (const a of attempts) if (a.passed) set.add(a.quiz_slug);
  return Array.from(set);
}

function bestPct(attempts: QuizAttempt[]): number {
  let best = 0;
  for (const a of attempts) {
    if (a.total > 0) best = Math.max(best, a.score / a.total);
  }
  return best;
}

// Per-path certificate badges
const PATH_BADGES: Badge[] = LEARN_PATHS.map((p) => ({
  key: `cert-${p.slug}`,
  title: p.certificateTitle,
  description: `Complete every lesson in the "${p.title}" path.`,
  icon: p.icon,
  accent: p.accent,
  tile: 'bg-ink-amber/15',
  category: 'Certificate',
  isEarned: ({ isComplete }) => pathProgress(p, isComplete).pct === 100,
  progress: ({ isComplete }) => pathProgress(p, isComplete).pct / 100,
}));

export const BADGES: Badge[] = [
  ...PATH_BADGES,
  {
    key: 'first-steps',
    title: 'First Steps',
    description: 'Complete your very first lesson.',
    icon: Footprints,
    accent: 'text-ink-emerald',
    tile: 'bg-ink-emerald/15',
    category: 'Milestone',
    isEarned: ({ isComplete }) =>
      LEARN_PATHS.some((p) => p.lessons.some((l) => isComplete(l.key))),
  },
  {
    key: 'anatomy-expert',
    title: 'Anatomy Expert',
    description: 'Tap through the interactive card anatomy explorer.',
    icon: ScrollText,
    accent: 'text-ink-sapphire',
    tile: 'bg-ink-sapphire/15',
    category: 'Milestone',
    isEarned: ({ isComplete }) => isComplete('anatomy-explorer'),
  },
  {
    key: 'coach-pet',
    title: "Coach's Pet",
    description: 'Finish the Sample Turn Simulator.',
    icon: GraduationCap,
    accent: 'text-primary',
    tile: 'bg-primary/15',
    category: 'Milestone',
    isEarned: ({ isComplete }) => isComplete('sim-first-turn'),
  },
  {
    key: 'quiz-whiz',
    title: 'Quiz Whiz',
    description: 'Pass any Learn quiz.',
    icon: Brain,
    accent: 'text-primary',
    tile: 'bg-primary/15',
    category: 'Quiz',
    isEarned: ({ attempts }) => passedQuizzes(attempts).length >= 1,
  },
  {
    key: 'triple-crown',
    title: 'Triple Crown',
    description: 'Pass three different Learn quizzes.',
    icon: Crown,
    accent: 'text-ink-ruby',
    tile: 'bg-ink-ruby/15',
    category: 'Quiz',
    isEarned: ({ attempts }) => passedQuizzes(attempts).length >= 3,
    progress: ({ attempts }) => Math.min(1, passedQuizzes(attempts).length / 3),
  },
  {
    key: 'perfectionist',
    title: 'Perfectionist',
    description: 'Score 100% on any Learn quiz.',
    icon: Sparkles,
    accent: 'text-ink-amber',
    tile: 'bg-ink-amber/15',
    category: 'Quiz',
    isEarned: ({ attempts }) => bestPct(attempts) >= 1,
    progress: ({ attempts }) => bestPct(attempts),
  },
  {
    key: 'all-paths',
    title: 'Grand Illumineer',
    description: 'Complete every learning path.',
    icon: Trophy,
    accent: 'text-ink-amber',
    tile: 'bg-ink-amber/20',
    category: 'Mastery',
    isEarned: ({ isComplete }) =>
      LEARN_PATHS.every((p) => pathProgress(p, isComplete).pct === 100),
    progress: ({ isComplete }) => {
      const totalPct = LEARN_PATHS.reduce(
        (acc, p) => acc + pathProgress(p, isComplete).pct,
        0,
      );
      return totalPct / (LEARN_PATHS.length * 100);
    },
  },
];

// Re-export for icon-only consumers
export const BADGE_ICONS = { Award, BookOpen, Layers, Trophy, Zap };
